import Button from '@/shared/Button';
import Div from '@/shared/Div';
import Form from '@/shared/Form';
import OtpInput from '@/shared/OtpInput';
import Paragraph from '@/shared/Paragraph';
import React, { memo, useEffect, useState } from 'react';
import { otpSend } from '@/actions/ecom/login';
import is from '@/utils/is';
import dlv from 'dlv';
import { RESEND_OTP_COUNTDOWN } from '@/constants/app';

type VerifyLeadRequest = {
  isLoading: boolean;
  isSuccess: boolean;
  error: any;
  isError: boolean;
  reset: () => void;
};

interface VerifyOTPProps {
  title?: string;
  subTitle?: string;
  mobile: string;
  onVerify: (otp: string) => void;
  verifyBtnLabel?: string;
  boxNumber?: number;
  isLoading?: boolean;
  verifyLeadRequest: VerifyLeadRequest;
  email?: string;
}

const VerifyOTP = ({
  title,
  subTitle,
  mobile,
  onVerify,
  verifyBtnLabel = 'Verify',
  boxNumber = 4,
  verifyLeadRequest,
  email,
}: VerifyOTPProps) => {
  const [resendCountdown, setResendCountdown] = useState(RESEND_OTP_COUNTDOWN);
  const [disableResend, setResendDisable] = useState(true);
  const [emptyInputBox, setEmptyInputBox] = useState(false);

  const [otp, setOtp] = useState('');
  useEffect(() => {
    let countdownTimer: NodeJS.Timeout;
    if (disableResend && resendCountdown > 0) {
      countdownTimer = setTimeout(() => {
        setResendCountdown((prevCount) => prevCount - 1);
      }, 1000);
    } else if (resendCountdown === 0) {
      setResendDisable(false);
    }
    return () => {
      clearTimeout(countdownTimer);
    };
  }, [disableResend, resendCountdown]);

  const resendOtp = async () => {
    try {
      verifyLeadRequest.reset();
      setOtp('');
      setEmptyInputBox(true);
      let resendData: { mobile_number: string; email?: string } = { mobile_number: mobile };
      if (email) {
        resendData.email = email;
      }

      await otpSend(resendData);
      setResendCountdown(RESEND_OTP_COUNTDOWN);
      setResendDisable(true);
      setEmptyInputBox(false);
    } catch (error) {
      console.error('Error resending OTP:', error);
    }
  };

  const verify = async () => {
    if (is.valid.otp(otp, boxNumber)) {
      setResendDisable(true);
      onVerify(otp);
    }
  };

  useEffect(() => {
    if (verifyLeadRequest.isError) {
      setOtp('');
      setEmptyInputBox(true);
    }
  }, [verifyLeadRequest.isError]);

  const setOTP = () => setOtp;
  return (
    <Div className="flex flex-col gap-s4">
      <Paragraph className="text-h-2  md:text-hd-2 ">{title}</Paragraph>
      {subTitle ? (
        <Div className="text-body-lg" dangerouslySetInnerHTML={{ __html: subTitle }} />
      ) : null}
      <Button
        theme="link"
        size="sm"
        className="w-fit !px-0"
        onClick={resendOtp}
        disabled={disableResend}
        data-testid="resendOtp"
      >
        {resendCountdown ? `Resend OTP (${resendCountdown}s)` : 'Resend OTP'}
      </Button>
      <Form className="flex flex-col gap-s6">
        <Div>
          <OtpInput
            data-testid="otpInput"
            name="otpLogin"
            boxNumber={boxNumber}
            value={otp}
            onSet={setOTP()}
            emptyInputBox={emptyInputBox}
            data={{
              success: verifyLeadRequest.isSuccess,
              message: dlv(verifyLeadRequest.error, 'error', ''),
            }}
          />
          <Paragraph className="text-red-500 text-body-base mt-s2">
            {dlv(verifyLeadRequest.error, 'error', '')}
          </Paragraph>
        </Div>
        <Button
          size="sm"
          className="w-full"
          onClick={verify}
          disabled={!is.valid.otp(otp, boxNumber)}
          isLoading={verifyLeadRequest.isLoading}
        >
          {verifyBtnLabel}
        </Button>
      </Form>
    </Div>
  );
};

export default memo(VerifyOTP);
